import { useQuery } from "@apollo/client";
import {
  Box,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  useMantineTheme,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { RETAILER_TRANSACTIONS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import TotalAmount from "components/controls/TotalAmount";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import ExportComponent from "utiles/export";
import { formatLocalDate, formatNumber } from "utiles/url";
import DetaileTable from "./detailTable";

const RetailerTransactions = () => {
  const [size, setSize] = useState("10");
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [retailer, setRetailer] = useState(null);
  const [retailerName, setRetailerName] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const theme = useMantineTheme();
  const [id, editId] = useState(null);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [detailUserName, setDetailUserName] = useState(null);
  const [detailTotalAmount, setDetailTotalAmount] = useState(null);

  const { data, loading, fetchMore, refetch } = useQuery(
    RETAILER_TRANSACTIONS,
    {
      variables: {
        first: parseInt(size),
        page: activePage,
        retailer_id: retailer ? retailer : null,
        period: timeRange || undefined,
        startDate: selectedStartDate
          ? formatLocalDate(selectedStartDate)
          : undefined,
        endDate: selectedEndDate ? formatLocalDate(selectedEndDate) : undefined,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to get Drivers Transactions ",
        });
      },
    },
  );

  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.getRetailerTransactionReport.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, []);
  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: parseInt(size),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };
  if (!hasMounted) {
    return null;
  }

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "retailer_name",
      sortable: false,
      searchable: false,
      width: "20%",
      render: (rowData) => {
        return <span>{rowData.retailer.user.name}</span>;
      },
    },
    {
      label: "Phone",
      key: "phone",
      sortable: false,
      searchable: false,
      width: "20%",
      render: (rowData) => {
        return <span>{rowData.retailer.user.phone}</span>;
      },
    },
    {
      label: "Amount",
      key: "amount",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span style={{ width: "120px" }}>
            {formatNumber(rowData.total_amount)}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ width: "150px", display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleViewDetails(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
          </div>
        );
      },
    },
  ];
  const handleViewDetails = (rowData) => {
    editId(rowData.transactions);
    setDetailUserName(rowData.retailer?.user?.name || null);
    setDetailTotalAmount(rowData.total_amount ?? null);
    setOpenedDetail(true);
  };
  const renderFilters = () => (
    <Box style={{ position: "relative", zIndex: 10000 }}>
      <Group
        justify="space-between"
        align="flex-start"
        style={{
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: "16px",
          alignItems: "center",
        }}
      >
        {/*<RetailerFilter
          valueType="id"
          setRetailerName={setRetailerName}
          retailer={retailer}
          onCardClick={setRetailer}
        />
        */}
        <Select
          style={{ minWidth: "140px" }}
          data={[
            { value: "DAILY", label: "Daily" },
            { value: "WEEKLY", label: "Weekly" },
            { value: "MONTHLY", label: "Monthly" },
            { value: "QUARTERLY", label: "Half Year" },
            { value: "YEARLY", label: "Yearly" },
          ]}
          value={timeRange}
          onChange={(value) => setTimeRange(value)}
          placeholder="Period"
          withinPortal
          clearable
        />
        <DatePicker
          style={{ minWidth: "140px" }}
          value={selectedStartDate}
          onChange={setSelectedStartDate}
          placeholder="Start Date"
          popoverProps={{ withinPortal: true, zIndex: 10001 }}
          clearable
        />
        <DatePicker
          style={{ minWidth: "140px" }}
          value={selectedEndDate}
          onChange={setSelectedEndDate}
          placeholder="End Date"
          popoverProps={{ withinPortal: true, zIndex: 10001 }}
          clearable
        />
        <ExportComponent
          data={data ? data.getRetailerTransactionReport.data : []}
          headers={headerData}
          reportTitle="Retailer Transactions Report"
          filterSubtitle={`${
            retailerName ? `Retailer: ${retailerName.label}` : "All Retailers"
          }`}
          fileNamePrefix="retailer_transactions_report"
        />
      </Group>
    </Box>
  );
  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      {renderFilters()}
      <Card shadow="sm" p="lg">
        <Drawer
          opened={openedDetail}
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          padding="xl"
          onClose={() => setOpenedDetail(false)}
          position="bottom"
          size="80%"
        >
          <DetaileTable
            ordersList={id}
            userName={detailUserName}
            totalAmount={detailTotalAmount}
          />
        </Drawer>
        <ScrollArea style={{ zIndex: 1 }}>
          <B2bTable
            total={total}
            paginatorInfo={data?.getRetailerTransactionReport?.paginatorInfo}
            searchInput={false}
            activePage={activePage}
            filterData={() => null}
            handelSearch={false}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            loading={loading}
            data={data ? data.getRetailerTransactionReport.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
          <TotalAmount
            amount={
              data
                ? data.getRetailerTransactionReport.all_retailers_total_amount
                : 0
            }
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default RetailerTransactions;
